import React, { useMemo, useCallback, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../i18n/resolve-key';
import {
  TableBody as MuiTableBody,
  TableRow,
  TableCell,
  Skeleton,
  Alert,
  Box,
  Button,
  Checkbox,
} from '@mui/material';
import type {
  TableDescriptor,
  ActionDescriptor,
  Locale,
  ReferenceDisplayValue,
} from '@finance/contracts';
import type { EvaluationContext } from '../../../runtime/conditions/evaluate-condition';
import { resolveAction, type ResolvedAction } from '../../../runtime/actions/action-resolution';
import { formatValue } from '../../../runtime/formatting/index.ts';
import { formatColumnValue } from './table-value-format.ts';
import { TableCellRenderer } from './TableCell.tsx';
import { TableRowActions } from './TableRowActions.tsx';
import type { TableState } from './table-state.ts';
import { getVisibleOrderedColumns, getPinnedColumnOffsets } from './table-state.ts';
import { isInteractiveTableTarget } from './row-interactions.ts';
import { tableTokens } from './table-tokens.js';
import { TableEmptyState } from './TableEmptyState.tsx';
import type { TableActionContext } from '../TableRenderer.tsx';

interface TableBodyProps {
  descriptor: TableDescriptor;
  data: Record<string, unknown>[];
  state: TableState;
  loading: boolean;
  error: Error | null;
  stale: boolean;
  degraded: boolean;
  onRetry?: () => void;
  onAction?: (action: ResolvedAction, context: TableActionContext) => void | Promise<void>;
  onRowActivate?: (row: Record<string, unknown>, resolvedAction?: ResolvedAction) => void;
  rowActivationAction?: ActionDescriptor;
  rowIdField?: string;
  getRowId?: (row: Record<string, unknown>) => string | undefined;
  onToggleRowSelection?: (id: string) => void;
  locale: Locale;
  evalCtx: EvaluationContext;
  referenceDisplayMap?: Record<string, Record<string, ReferenceDisplayValue>> | null;
  aggregates?: Record<string, unknown> | null;
  groupMetadata?: Record<string, unknown> | null;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  virtualize?: boolean;
  virtualizeOverscan?: number;
}

interface VirtualizedRowsProps {
  data: Record<string, unknown>[];
  scrollRef: React.RefObject<HTMLDivElement | null>;
  overscan: number;
  estimateSize: () => number;
  getItemKey: (index: number) => string;
  renderRow: (
    row: Record<string, unknown>,
    index: number,
    key: string,
    measureRef?: React.Ref<HTMLTableRowElement>,
  ) => React.ReactNode;
  colSpan: number;
}

function VirtualizedRows({
  data,
  scrollRef,
  overscan,
  estimateSize,
  getItemKey,
  renderRow,
  colSpan,
}: VirtualizedRowsProps) {
  const getScrollElement = useCallback(() => scrollRef.current, [scrollRef]);
  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement,
    estimateSize,
    overscan,
    getItemKey,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length > 0
    ? totalSize - virtualItems[virtualItems.length - 1].end
    : 0;

  return (
    <>
      {paddingTop > 0 && (
        <TableRow aria-hidden key="virtual-top-spacer" sx={{ height: paddingTop }}>
          <TableCell colSpan={colSpan} sx={{ p: 0, border: 'none' }}>
            <Box aria-hidden sx={{ height: paddingTop }} />
          </TableCell>
        </TableRow>
      )}
      {virtualItems.map(virtualRow => {
        const measureElement = (
          virtualRow as unknown as { measureElement?: React.Ref<HTMLTableRowElement> }
        ).measureElement;
        return renderRow(
          data[virtualRow.index],
          virtualRow.index,
          String(virtualRow.key),
          measureElement,
        );
      })}
      {paddingBottom > 0 && (
        <TableRow aria-hidden key="virtual-bottom-spacer" sx={{ height: paddingBottom }}>
          <TableCell colSpan={colSpan} sx={{ p: 0, border: 'none' }}>
            <Box aria-hidden sx={{ height: paddingBottom }} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function TableBody({
  descriptor,
  data,
  state,
  loading,
  error,
  onRetry,
  onAction,
  onRowActivate,
  rowActivationAction,
  rowIdField = 'id',
  getRowId,
  locale,
  evalCtx,
  referenceDisplayMap,
  onToggleRowSelection,
  aggregates,
  groupMetadata,
  scrollRef,
  virtualize,
  virtualizeOverscan,
}: TableBodyProps) {
  const { t } = useTranslation();
  const rowIdFieldName = descriptor.rowIdField ?? rowIdField ?? 'id';
  const getRowIdValue = useCallback(
    (row: Record<string, unknown>): string | undefined => {
      const raw = getRowId ? getRowId(row) : row[rowIdFieldName];
      return raw == null ? undefined : String(raw);
    },
    [getRowId, rowIdFieldName],
  );

  const visibleColumns = useMemo(
    () => getVisibleOrderedColumns(descriptor, state),
    [descriptor, state],
  );
  const columnOffsets = useMemo(
    () => getPinnedColumnOffsets(
      visibleColumns,
      state.pinnedColumns.left,
      state.pinnedColumns.right,
      state.columnSizes,
    ),
    [visibleColumns, state.columnSizes, state.pinnedColumns.left, state.pinnedColumns.right],
  );
  const pinnedLeftSet = useMemo(() => new Set(state.pinnedColumns.left), [state.pinnedColumns.left]);
  const pinnedRightSet = useMemo(() => new Set(state.pinnedColumns.right), [state.pinnedColumns.right]);

  const selectionEnabled =
    (descriptor.selectionMode === 'single' || descriptor.selectionMode === 'multiple') &&
    descriptor.capabilities?.selection === true &&
    (descriptor.bulkActions?.length ?? 0) > 0;
  const hasRowActions = (descriptor.rowActions ?? []).length > 0;
  const colSpan = visibleColumns.length + (selectionEnabled ? 1 : 0) + (hasRowActions ? 1 : 0);
  const selectedIds = state.selection.selectedIds;
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  const activeGroup = state.grouping[0];
  const groupedRows = useMemo(() => {
    if (!activeGroup) return null;
    const groups = new Map<string, { value: unknown; rows: Record<string, unknown>[] }>();
    for (const row of data) {
      const value = row[activeGroup.field];
      const key = JSON.stringify({ [activeGroup.field]: value ?? null });
      const group = groups.get(key) ?? { value, rows: [] };
      group.rows.push(row);
      groups.set(key, group);
    }
    return [...groups.entries()].map(([key, group]) => ({ key, ...group }));
  }, [activeGroup, data]);
  const groupMetadataByKey = useMemo(() => {
    const groups = groupMetadata?.groups;
    if (!Array.isArray(groups)) return new Map<string, Record<string, unknown>>();
    return new Map(
      groups.flatMap(group => {
        if (!group || typeof group !== 'object') return [];
        const entry = group as Record<string, unknown>;
        return typeof entry.key === 'string' ? [[entry.key, entry] as const] : [];
      }),
    );
  }, [groupMetadata]);

  const resolveRowActivation = useCallback(
    (row: Record<string, unknown>) => {
      if (!rowActivationAction) return undefined;
      const resolved = resolveAction({ action: rowActivationAction, ctx: evalCtx, data: row });
      return resolved.visible && !resolved.disabled && resolved.available ? resolved : undefined;
    },
    [evalCtx, rowActivationAction],
  );

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>, row: Record<string, unknown>) => {
      if (!onRowActivate || isInteractiveTableTarget(event.target)) return;
      if (!rowActivationAction) {
        onRowActivate(row);
        return;
      }
      const resolved = resolveRowActivation(row);
      if (resolved) onRowActivate(row, resolved);
    },
    [onRowActivate, resolveRowActivation, rowActivationAction],
  );

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent, row: Record<string, unknown>) => {
      if (isInteractiveTableTarget(event.target)) return;
      if (!onRowActivate || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      if (!rowActivationAction) {
        onRowActivate(row);
        return;
      }
      const resolved = resolveRowActivation(row);
      if (resolved) onRowActivate(row, resolved);
    },
    [onRowActivate, resolveRowActivation, rowActivationAction],
  );

  const getItemKey = useCallback(
    (index: number) => getRowIdValue(data[index]) ?? `row-${index}`,
    [data, getRowIdValue],
  );
  const estimateSize = useCallback(
    () => tableTokens.density[state.density]?.rowHeight ?? tableTokens.density.standard.rowHeight,
    [state.density],
  );

  const renderRow = (
    row: Record<string, unknown>,
    rowIndex: number,
    rowKey: string,
    measureRef?: React.Ref<HTMLTableRowElement>,
  ) => {
    const id = getRowIdValue(row);
    if (id === undefined) {
      if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
        throw new Error(
          `Invariant violation: row identity missing at index ${rowIndex} for table ${String(descriptor.id)}`,
        );
      }
      return null;
    }
    const resolvedActivation = rowActivationAction ? resolveRowActivation(row) : undefined;
    const rowActivatable = Boolean(onRowActivate && (!rowActivationAction || resolvedActivation));
    const isSelected = selectedIds.includes(id);
    return (
      <TableRow
        key={rowKey}
        ref={measureRef}
        data-row-id={id}
        data-index={rowIndex}
        hover={rowActivatable}
        onClick={event => handleRowClick(event, row)}
        onKeyDown={event => handleRowKeyDown(event, row)}
        tabIndex={rowActivatable ? 0 : -1}
        sx={rowActivatable ? {
          cursor: 'pointer',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '-2px',
          },
        } : undefined}
        selected={isSelected}
      >
        {selectionEnabled && (
          <TableCell padding="checkbox" onClick={event => event.stopPropagation()}>
            <Checkbox
              aria-label={t('common:table.selectRow')}
              checked={isSelected}
              onChange={() => onToggleRowSelection?.(id)}
              size="small"
            />
          </TableCell>
        )}
        {visibleColumns.map((column, columnIndex) => {
          const pinned = pinnedLeftSet.has(column.id)
            ? 'left'
            : pinnedRightSet.has(column.id)
              ? 'right'
              : 'center';
          const offset = columnOffsets[columnIndex];
          const columnWidth = state.columnSizes.find(size => size.columnId === column.id)?.width ?? column.defaultWidth ?? 150;
          return (
            <TableCellRenderer
              key={column.id}
              column={column}
              row={row}
              evalCtx={evalCtx}
              locale={locale}
              onAction={onAction}
              referenceDisplayMap={referenceDisplayMap}
              pinned={pinned}
              leftOffset={offset?.left ?? 0}
              rightOffset={offset?.right ?? 0}
              width={columnWidth}
            />
          );
        })}
        {hasRowActions && (
          <TableCell
            onClick={event => event.stopPropagation()}
            data-table-interactive="true"
            sx={{
              position: 'sticky',
              right: 0,
              zIndex: tableTokens.zIndex.pinnedBody,
              backgroundColor: tableTokens.pinned.bodyBg,
              boxShadow: tableTokens.pinned.shadowLeft,
              minWidth: tableTokens.actionColumn.minWidth,
              width: tableTokens.actionColumn.width,
            }}
          >
            <TableRowActions
              actions={descriptor.rowActions!}
              row={row}
              evalCtx={evalCtx}
              onAction={onAction}
            />
          </TableCell>
        )}
      </TableRow>
    );
  };

  if (loading && data.length === 0) {
    return (
      <MuiTableBody>
        {Array.from({ length: 5 }).map((_, index) => (
          <TableRow key={index}>
            {selectionEnabled && (
              <TableCell padding="checkbox"><Skeleton variant="text" width={20} /></TableCell>
            )}
            {visibleColumns.map(column => (
              <TableCell key={column.id}><Skeleton variant="text" width="80%" /></TableCell>
            ))}
            {hasRowActions && (
              <TableCell
                sx={{
                  position: 'sticky',
                  right: 0,
                  zIndex: tableTokens.zIndex.pinnedBody,
                  backgroundColor: tableTokens.pinned.bodyBg,
                  boxShadow: tableTokens.pinned.shadowLeft,
                  minWidth: tableTokens.actionColumn.minWidth,
                  width: tableTokens.actionColumn.width,
                }}
              />
            )}
          </TableRow>
        ))}
      </MuiTableBody>
    );
  }

  if (error) {
    return (
      <MuiTableBody>
        <TableRow>
          <TableCell colSpan={colSpan}>
            <Alert
              severity="error"
              action={onRetry ? <Button onClick={onRetry}>{t('common:retry')}</Button> : undefined}
            >
              {t('common:states.error.message')}
            </Alert>
          </TableCell>
        </TableRow>
      </MuiTableBody>
    );
  }

  if (data.length === 0) {
    // Empty-state action identity is structural, never inferred from localized
    // label text. Import descriptors use the canonical upload icon/kind.
    const emptyDescriptors: ActionDescriptor[] = [];
    if (descriptor.createAction) emptyDescriptors.push(descriptor.createAction);
    emptyDescriptors.push(
      ...(descriptor.pageActions ?? []).filter(
        action => action.iconKey === 'upload' || action.kind === 'upload',
      ),
    );
    const resolvedEmptyActions = [...new Map(
      emptyDescriptors.map(action => [String(action.id), resolveAction({ action, ctx: evalCtx })]),
    ).values()].filter(action => action.visible);
    const isReadOnly =
      descriptor.capabilities?.create !== true &&
      descriptor.capabilities?.update !== true &&
      descriptor.capabilities?.delete !== true;

    const title = descriptor.emptyState?.emptyKey
      ? t(resolveTKey(descriptor.emptyState.emptyKey))
      : undefined;
    const message = descriptor.emptyState?.noResultsKey
      ? t(resolveTKey(descriptor.emptyState.noResultsKey))
      : t(resolveTKey(descriptor.emptyStateKey ?? 'common:states.empty.message'));
    const emptyContext: TableActionContext = {
      source: 'empty-state',
      tableId: String(descriptor.id),
      selectedIds: [],
    };

    return (
      <MuiTableBody>
        <TableRow>
          <TableCell colSpan={colSpan} sx={{ border: 0 }}>
            <TableEmptyState
              title={title}
              message={message}
              actions={resolvedEmptyActions}
              actionContext={{ source: 'page', tableId: String(descriptor.id), selectedIds: [] }}
              onAction={
                onAction
                  ? (resolved, executionContext) =>
                      onAction(resolved, {
                        ...emptyContext,
                        confirmationHandled: executionContext?.confirmationHandled,
                        confirmationValues: executionContext?.confirmationValues,
                      })
                  : undefined
              }
              readOnly={isReadOnly}
            />
          </TableCell>
        </TableRow>
      </MuiTableBody>
    );
  }

  return (
    <MuiTableBody>
      {virtualize && scrollRef && !groupedRows ? (
        <VirtualizedRows
          data={data}
          scrollRef={scrollRef}
          overscan={virtualizeOverscan ?? 5}
          estimateSize={estimateSize}
          getItemKey={getItemKey}
          renderRow={renderRow}
          colSpan={colSpan}
        />
      ) : groupedRows ? (
        groupedRows.flatMap(group => {
          const metadata = groupMetadataByKey.get(group.key);
          const metadataCount = typeof metadata?.count === 'number' ? metadata.count : group.rows.length;
          const aggregateValues = metadata?.aggregates;
          const aggregateText = aggregateValues && typeof aggregateValues === 'object'
            ? Object.entries(aggregateValues as Record<string, unknown>)
                .map(([field, value]) => {
                  const column = descriptor.columns.find(candidate => candidate.field === field);
                  return column
                    ? `${t(resolveTKey(column.labelKey))}: ${formatColumnValue(value, column, { locale })}`
                    : '';
                })
                .filter(Boolean)
                .join(' · ')
            : '';
          const groupColumn = descriptor.columns.find(column => column.field === activeGroup?.field);
          const groupLabel = groupColumn ? t(resolveTKey(groupColumn.labelKey)) : activeGroup?.field;
          const displayValue = groupColumn
            ? formatColumnValue(group.value, groupColumn, { locale })
            : String(group.value ?? '—');
          const displayCount = formatValue(metadataCount, { formatType: 'integer' }, { locale });
          const collapsed = collapsedGroups.has(group.key);
          return [
            <TableRow key={`group-${group.key}`} data-testid="table-group-row" sx={{ backgroundColor: 'action.hover' }}>
              <TableCell colSpan={colSpan} component="th" scope="rowgroup">
                <Button
                  size="small"
                  variant="text"
                  onClick={() =>
                    setCollapsedGroups(current => {
                      const next = new Set(current);
                      if (next.has(group.key)) next.delete(group.key);
                      else next.add(group.key);
                      return next;
                    })
                  }
                  aria-expanded={!collapsed}
                  aria-label={collapsed ? t(resolveTKey('table.expandGroup')) : t(resolveTKey('table.collapseGroup'))}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left', whiteSpace: 'normal' }}
                >
                  {`${String(groupLabel)}: ${displayValue} (${displayCount})${aggregateText ? ` — ${aggregateText}` : ''}`}
                </Button>
              </TableCell>
            </TableRow>,
            ...(collapsed
              ? []
              : group.rows.map((row, index) =>
                  renderRow(row, index, getRowIdValue(row) ?? `${group.key}-${index}`),
                )),
          ];
        })
      ) : (
        data.map((row, rowIndex) => renderRow(row, rowIndex, getItemKey(rowIndex)))
      )}

      {aggregates && (
        <TableRow sx={{ backgroundColor: 'action.hover' }}>
          {selectionEnabled && <TableCell padding="checkbox" component="td" />}
          {visibleColumns.map((column, index) => {
            const pinned = pinnedLeftSet.has(column.id)
              ? 'left'
              : pinnedRightSet.has(column.id)
                ? 'right'
                : 'center';
            const offset = columnOffsets[index];
            const columnWidth = state.columnSizes.find(size => size.columnId === column.id)?.width ?? column.defaultWidth ?? 150;
            const value = aggregates[column.field] ?? aggregates[column.id];
            return (
              <TableCell
                key={`agg-${column.id}`}
                component="td"
                sx={{
                  width: columnWidth,
                  minWidth: columnWidth,
                  position: pinned !== 'center' ? 'sticky' : undefined,
                  left: pinned === 'left' ? `${offset.left}px` : undefined,
                  right: pinned === 'right' ? `${offset.right}px` : undefined,
                  zIndex: pinned !== 'center' ? tableTokens.zIndex.aggregate : undefined,
                  backgroundColor: pinned !== 'center' ? 'action.hover' : undefined,
                  ...(pinned === 'left' && { boxShadow: tableTokens.pinned.shadowRight }),
                  ...(pinned === 'right' && { boxShadow: tableTokens.pinned.shadowLeft }),
                  fontWeight: 700,
                }}
              >
                {value != null ? formatColumnValue(value, column, { locale }) : ''}
              </TableCell>
            );
          })}
          {hasRowActions && (
            <TableCell
              component="td"
              sx={{
                position: 'sticky',
                right: 0,
                zIndex: tableTokens.zIndex.aggregate,
                backgroundColor: 'action.hover',
                boxShadow: tableTokens.pinned.shadowLeft,
                minWidth: tableTokens.actionColumn.minWidth,
                width: tableTokens.actionColumn.width,
              }}
            />
          )}
        </TableRow>
      )}
    </MuiTableBody>
  );
}
